/**
 * Wraps one template renderer so a malformed or unexpectedly-shaped spec renders an inline
 * "couldn't render this view" card instead of blanking the page around it — there is no error
 * boundary anywhere else in the app, and a spec is agent-authored input, not trusted code.
 *
 * Error boundaries are still class-only in React; there is no hook equivalent.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

import { logger } from "@/lib/logger";

interface ViewErrorBoundaryProps {
  children: ReactNode;
  /** Reset the boundary when this changes — a different spec deserves a fresh render attempt
   *  rather than staying stuck on a stale error (e.g. the same card re-used for a new pin). */
  resetKey: string;
}

interface ViewErrorBoundaryState {
  error: Error | null;
  resetKey: string;
}

export class ViewErrorBoundary extends Component<ViewErrorBoundaryProps, ViewErrorBoundaryState> {
  state: ViewErrorBoundaryState = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromProps(
    props: ViewErrorBoundaryProps,
    state: ViewErrorBoundaryState,
  ): Partial<ViewErrorBoundaryState> | null {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }

  static getDerivedStateFromError(error: Error): Partial<ViewErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error("[views] failed to render a view", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <p className="text-destructive text-xs">
          Couldn&apos;t render this view — its spec may be malformed.
        </p>
      );
    }
    return this.props.children;
  }
}
